import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { useClean, useFeed, useHome, useRevive } from "../api/hooks";
import { ActivityFeed } from "../components/ActivityFeed";
import { CornerTicks } from "../components/CornerTicks";
import { Meter } from "../components/Meter";
import { Toast } from "../components/Toast";
import { XpMeter } from "../components/XpMeter";
import { Button, Card, ErrorNotice, Loading, SectionLabel } from "../components/primitives";
import { moodLabel } from "../lib/format";
import { theme } from "../theme";

const MASCOT = "https://racccoin.org/raccmascot_anim.webp";

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(5)};
`;

const StatusStrip = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 12px;
  color: ${({ theme }) => theme.color.textDim};
`;

const Awake = styled.span<{ $awake: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme, $awake }) => ($awake ? theme.color.satiety : theme.color.asleep)};

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
    box-shadow: 0 0 8px currentColor;
  }
`;

const HeroCard = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 18px 18px 20px;
  border-radius: ${({ theme }) => theme.radius.xxl};
  border: 1px solid ${({ theme }) => theme.color.border};
  background:
    radial-gradient(120% 70% at 50% 0%, rgba(0, 152, 234, 0.18) 0%, transparent 60%),
    ${({ theme }) => theme.color.surface};
`;

const HeroTop = styled.div`
  width: 100%;
`;

const Holders = styled.div`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  letter-spacing: 0.03em;
  color: ${({ theme }) => theme.color.textDim};
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const Name = styled.h1`
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.8px;
  margin: 2px 0 0;
`;

// Level badge chip pinned to the right of Rocky's name (accent-toned pill).
const LevelBadge = styled.span`
  flex-shrink: 0;
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: ${({ theme }) => theme.color.accent};
  background: ${({ theme }) => theme.color.accentSoft};
  border: 1px solid rgba(0, 152, 234, 0.35);
  border-radius: ${({ theme }) => theme.radius.pill};
  padding: 3px 10px;
`;

const Mascot = styled.img`
  width: 196px;
  height: 196px;
  object-fit: contain;
  margin: 6px 0 4px;
`;

const MoodPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: rgba(0, 0, 0, 0.5);
  border: 1px solid ${({ theme }) => theme.color.border};
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textDim};
`;

const MoodDot = styled.span<{ $awake: boolean }>`
  color: ${({ theme, $awake }) => ($awake ? theme.color.satiety : theme.color.asleep)};
`;

const Section = styled.div``;

const LabelRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const Note = styled.span`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 11px;
  color: ${({ theme }) => theme.color.textFaint};
`;

const Meters = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space(3)};
`;

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.space(2)};

  & + & {
    margin-top: ${({ theme }) => theme.space(2)};
  }
`;

// feed / clean get a saturated gradient + glow (design's primary tone buttons).
const ColorBtn = styled.button<{ $tone: "satiety" | "hygiene" }>`
  appearance: none;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 14px 16px;
  font-family: ${({ theme }) => theme.font.ui};
  font-weight: 600;
  font-size: 15px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${({ theme, $tone }) =>
    $tone === "satiety"
      ? `linear-gradient(180deg, ${theme.color.satiety}, #2bb874)`
      : `linear-gradient(180deg, ${theme.color.hygiene}, #36a4d8)`};
  box-shadow: ${({ theme, $tone }) =>
    `0 8px 24px ${$tone === "satiety" ? theme.color.satiety : theme.color.hygiene}44, inset 0 1px 0 rgba(255, 255, 255, 0.2)`};
  transition: transform 0.06s ease, opacity 0.15s ease;

  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Glyph = styled.span<{ $color?: string }>`
  font-family: ${({ theme }) => theme.font.mono};
  font-size: 17px;
  color: ${({ $color }) => $color ?? "inherit"};
`;

const Flavor = styled.div`
  font-size: 14px;
  line-height: 1.5;
  color: ${({ theme }) => theme.color.text};
`;

export function Home() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data, isLoading, error } = useHome();
  const feed = useFeed();
  const clean = useClean();
  const revive = useRevive();
  const [toast, setToast] = useState<string | null>(null);

  if (isLoading) return <Loading label="checking on rocky…" />;
  if (error) return <ErrorNotice message={error.message} />;
  if (!data) return null;

  const { pet, holderCount, decayIntervalMinutes, activity } = data;
  const dead = pet.dead;
  const busy = feed.isPending || clean.isPending || revive.isPending;

  function openRiddle() {
    navigate("/riddle", { state: { backgroundLocation: location } });
  }

  return (
    <Stack>
      <StatusStrip>
        <span>// rocky.v1 · the den</span>
        <Awake $awake={pet.awake}>{pet.awake ? "awake" : "asleep"}</Awake>
      </StatusStrip>

      <HeroCard>
        <CornerTicks />
        <HeroTop>
          <Holders>raised by {holderCount.toLocaleString()} holders</Holders>
          <NameRow>
            <Name>Rocky</Name>
            <LevelBadge>lv {pet.level}</LevelBadge>
          </NameRow>
        </HeroTop>
        <Mascot src={MASCOT} alt="Rocky" />
        <MoodPill>
          <MoodDot $awake={pet.awake}>●</MoodDot>
          mood: {moodLabel(pet.mood)} · day {pet.ageDays}
        </MoodPill>
      </HeroCard>

      <Section>
        <LabelRow>
          <SectionLabel>// meters</SectionLabel>
          <Note>decays every {decayIntervalMinutes}m</Note>
        </LabelRow>
        <Card>
          <Meters>
            <Meter label="satiety" value={pet.satiety} color={theme.color.satiety} icon="◍" />
            <Meter label="hygiene" value={pet.hygiene} color={theme.color.hygiene} icon="◇" />
            <Meter
              label="happiness"
              value={pet.happiness}
              color={theme.color.happiness}
              icon="✦"
            />
            <XpMeter pet={pet} />
          </Meters>
        </Card>
      </Section>

      <Section>
        <SectionLabel>// actions</SectionLabel>
        {dead ? (
          <Card>
            {revive.error && <ErrorNotice message={revive.error.message} />}
            <Button $full disabled={busy} onClick={() => revive.mutate()}>
              {revive.isPending ? "reviving…" : "Revive Rocky"}
            </Button>
          </Card>
        ) : (
          <>
            <ActionRow>
              <ColorBtn
                $tone="satiety"
                disabled={busy}
                onClick={() =>
                  feed.mutate(undefined, {
                    onSuccess: (r) => {
                      if (r.rateLimited) setToast("rocky's full for today — try again tomorrow.");
                    },
                  })
                }
              >
                <Glyph>◍</Glyph> {feed.isPending ? "feeding…" : "/feed"}
              </ColorBtn>
              <ColorBtn
                $tone="hygiene"
                disabled={busy}
                onClick={() =>
                  clean.mutate(undefined, {
                    onSuccess: (r) => {
                      if (r.rateLimited) setToast("rocky's squeaky clean — try again tomorrow.");
                    },
                  })
                }
              >
                <Glyph>◇</Glyph> {clean.isPending ? "cleaning…" : "/clean"}
              </ColorBtn>
            </ActionRow>
            <ActionRow>
              <Button $variant="ghost" onClick={openRiddle}>
                <Glyph $color={theme.color.happiness}>?</Glyph> /riddle
              </Button>
              <Button $variant="ghost" onClick={() => navigate("/quest")}>
                <Glyph $color={theme.color.accent}>⌬</Glyph> /quest
              </Button>
            </ActionRow>
          </>
        )}
      </Section>

      {(feed.data?.flavorText || clean.data?.flavorText) && (
        <Section>
          <SectionLabel>// rocky</SectionLabel>
          <Card>
            {feed.data?.flavorText && <Flavor>{feed.data.flavorText}</Flavor>}
            {clean.data?.flavorText && <Flavor>{clean.data.flavorText}</Flavor>}
          </Card>
        </Section>
      )}

      <Section>
        <LabelRow>
          <SectionLabel>// chatter</SectionLabel>
          <Note>last 4 events</Note>
        </LabelRow>
        <Card>
          <ActivityFeed events={activity.slice(0, 4)} />
        </Card>
      </Section>

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </Stack>
  );
}
